import { PlayerSummary } from '@/state/multiplayerTypes';

type SessionStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting';

interface TabletopTopBarProps {
  status: SessionStatus;
  players: PlayerSummary[];
  sessionId: string | null;
  onOpenPlayers: () => void;
  onOpenChat: () => void;
  onOpenMenu: () => void;
  onResetView: () => void;
  onToggleInvertView: () => void;
  invertView: boolean;
  hasUnreadChat?: boolean;
}

const statusLabel: Record<SessionStatus, string> = {
  idle: 'Offline',
  connecting: 'Connecting',
  connected: 'Live',
  reconnecting: 'Reconnecting'
};

const statusColor: Record<SessionStatus, string> = {
  idle: 'bg-slate-400',
  connecting: 'bg-amber-400',
  connected: 'bg-emerald-500',
  reconnecting: 'bg-amber-400'
};

export default function TabletopTopBar({
  status,
  players,
  sessionId,
  onOpenPlayers,
  onOpenChat,
  onOpenMenu,
  onResetView,
  onToggleInvertView,
  invertView,
  hasUnreadChat = false
}: TabletopTopBarProps) {
  return (
    <header className="table-topbar pointer-events-auto fixed left-3 right-3 top-3 z-50 flex h-14 items-center justify-between gap-3 px-3 sm:left-4 sm:right-auto sm:top-4 sm:h-16 sm:w-fit sm:max-w-[calc(100vw-2rem)] sm:justify-start sm:px-4">
      <div className="flex min-w-0 items-center gap-3">
        <img
          src="/wide-logo.png"
          alt="Dexsy"
          width={132}
          height={32}
          loading="eager"
          className="h-7 w-auto shrink-0 opacity-95 sm:h-8"
        />
        <div className="hidden h-8 w-px bg-slate-900/10 md:block dark:bg-white/10" />
        <button
          type="button"
          onClick={onOpenPlayers}
          className="hidden min-w-0 items-center gap-2 rounded-full px-2.5 py-1.5 text-left transition hover:bg-slate-900/5 md:flex dark:hover:bg-white/8"
        >
          <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${statusColor[status]}`} />
          <span className="min-w-0">
            <span className="block truncate text-xs font-semibold text-slate-900 dark:text-slate-100">
              {statusLabel[status]}{players.length > 0 ? ` · ${players.length} player${players.length === 1 ? '' : 's'}` : ''}
            </span>
            <span className="block max-w-[220px] truncate text-[10px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
              {sessionId || 'Local table'}
            </span>
          </span>
        </button>
      </div>

      <div className="flex items-center gap-1.5">
        <button type="button" onClick={onResetView} className="table-icon-button" aria-label="Reset view">
          ⌂
        </button>
        <button
          type="button"
          onClick={onToggleInvertView}
          className={`table-icon-button ${invertView ? 'table-icon-button-active' : ''}`}
          aria-label={invertView ? 'Restore view' : 'Invert view'}
        >
          ↻
        </button>
        <button type="button" onClick={onOpenChat} className="table-icon-button relative" aria-label="Open chat">
          ✉
          {hasUnreadChat && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-blue-500" />}
        </button>
        <button type="button" onClick={onOpenPlayers} className="table-icon-button md:hidden" aria-label="Open players">
          ●
        </button>
        <button type="button" onClick={onOpenMenu} className="table-icon-button" aria-label="Open menu">
          ☰
        </button>
      </div>
    </header>
  );
}
